import { useMemo, useState } from "react";
import { formatLatency, type ProcessEnd } from "@archlang/engine";

export interface EndPickerProps {
  readonly ends: readonly ProcessEnd[];
}

/**
 * v0.10.2 end picker (PROCESS.md §13, decision 10) — a process with several
 * ends (each `fail`/`finish` + the fall-through) lets you read each end's
 * worst-case estimate. Defaults to the longest end (the badge default). A
 * display-only reader in v1: the diagram already shows every end's row.
 */
export function EndPicker({ ends }: EndPickerProps) {
  const longestIdx = useMemo(() => Math.max(0, ends.findIndex((e) => e.longest)), [ends]);
  const [picked, setPicked] = useState(false);
  const [idx, setIdx] = useState(longestIdx);
  // Follow longest on in-place edits until the user explicitly picks an end.
  // Clamp also guards against a live model edit that shrinks/reorders the array.
  const eff = picked ? (idx >= 0 && idx < ends.length ? idx : longestIdx) : longestIdx;
  const sel = ends[eff];
  if (!sel) return null;
  const chip = sel.min !== sel.estimate ? `${formatLatency(sel.min)}–${formatLatency(sel.estimate)}` : formatLatency(sel.estimate);
  // Per-OPTION kind marker (✕ fail / ■ finish / ▸ fall-through) — computed
  // from each end, never from the selection (r43: every option carried the
  // selected end's icon, mislabeling the others' kinds).
  const iconOf = (kind: ProcessEnd["kind"]): string =>
    kind === "fail" ? "✕" : kind === "finish" ? "■" : "▸";
  return (
    <label className="inline-flex items-center gap-1 text-[11px] text-slate-400" title="Estimate for a specific process end (PROCESS.md §13)">
      <span className="text-slate-500">end</span>
      <select
        data-testid="process-end-picker"
        // Bound to the EFFECTIVE index, not the raw local state: until the user
        // picks, the picker follows the longest end across in-place edits — the
        // dropdown must name the same end the estimate chip prices (r43).
        value={eff}
        onChange={(e) => { setPicked(true); setIdx(Number(e.target.value)); }}
        className="rounded border border-slate-700 bg-slate-800/(--tint-strong) px-1 py-0.5 text-slate-300 cursor-pointer"
      >
        {ends.map((e, i) => (
          <option key={i} value={i}>
            {iconOf(e.kind)} {e.label}{e.longest ? " (longest)" : ""}
          </option>
        ))}
      </select>
      <span className="tabular-nums text-slate-300" data-testid="process-end-estimate">
        ~ {chip}
      </span>
    </label>
  );
}
